import * as Api from '@ViewModels';

function hasNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

function isPolicyCriterion(criteria?: Api.IContactFilterCriteria): boolean {
	return criteria?.property === Api.ContactFilterCriteriaProperty.Policy;
}

function isTagCriterion(criteria?: Api.IContactFilterCriteria): boolean {
	return criteria?.property === Api.ContactFilterCriteriaProperty.Tag;
}

function getTagValue(criteria?: Api.IContactFilterCriteria): string | undefined {
	if (!criteria) {
		return undefined;
	}
	if (hasNonEmptyString(criteria.value)) {
		return criteria.value.trim();
	}
	if (criteria.criteria?.length === 1 && hasNonEmptyString(criteria.criteria[0]?.value)) {
		return criteria.criteria[0].value.trim();
	}
	return undefined;
}

function getPolicyCriteriaValues(criteria?: Api.IContactFilterCriteria): Api.IContactFilterCriteria[] {
	if (!criteria?.criteria?.length) {
		if (hasNonEmptyString(criteria?.value)) {
			return [
				{
					property: Api.ContactFilterCriteriaProperty.Policy,
					value: criteria.value.trim(),
				},
			];
		}
		return [];
	}

	return criteria.criteria
		.filter(x => x.property === Api.ContactFilterCriteriaProperty.Policy && hasNonEmptyString(x.value))
		.map(x => ({
			property: Api.ContactFilterCriteriaProperty.Policy,
			value: x.value.trim(),
		}));
}

function isPrimaryCandidate(criteria?: Api.IContactFilterCriteria): boolean {
	if (!criteria) {
		return false;
	}

	// Policy primary should remain recognized even before a value is chosen,
	// so the Policy selector keeps rendering while users make a selection.
	if (isPolicyCriterion(criteria)) {
		return true;
	}

	if (!isTagCriterion(criteria)) {
		return false;
	}

	// Primary tag criteria is a direct tag criterion (no grouped nested criteria).
	// This supports both "has this tag" (AND) and "is missing this tag" (NOT).
	if (Array.isArray(criteria.criteria) && criteria.criteria.length > 0) {
		return false;
	}

	return true;
}

/**
 * Determines whether a trigger should use policy-first segment filtering.
 * Covers both Policy Renew (resource selector) and New Policy trigger types.
 */
export function isPolicyBasedSegmentTrigger(trigger?: Api.IAutomationTrigger): boolean {
	if (!trigger) {
		return false;
	}

	if (trigger._type === Api.AutomationTriggerType.NewPolicyTrigger) {
		return true;
	}

	return Api.VmUtils.Automations.triggers.isResourceSelectorTrigger(trigger, Api.ResourceSelectorId.PolicyRenew);
}

/**
 * Normalizes primary criteria for segment UI + persistence.
 */
export function normalizePrimaryCriteria(
	criteria?: Api.IContactFilterCriteria | null
): Api.IContactFilterCriteria | undefined {
	if (!criteria) {
		return undefined;
	}

	if (isPolicyCriterion(criteria)) {
		const policyValues = getPolicyCriteriaValues(criteria);
		return {
			property: Api.ContactFilterCriteriaProperty.Policy,
			op: criteria.op ?? Api.FilterOperator.Or,
			criteria: policyValues,
		};
	}

	if (isTagCriterion(criteria)) {
		return {
			property: Api.ContactFilterCriteriaProperty.Tag,
			op: criteria.op ?? Api.FilterOperator.And,
			value: getTagValue(criteria),
		};
	}

	return {
		...criteria,
	};
}

/**
 * Returns true when a primary segment criterion has a usable value.
 */
export function primaryCriteriaHasValue(criteria?: Api.IContactFilterCriteria | null): boolean {
	const normalized = normalizePrimaryCriteria(criteria);
	if (!normalized) {
		return false;
	}

	if (isPolicyCriterion(normalized)) {
		return getPolicyCriteriaValues(normalized).length > 0;
	}

	if (isTagCriterion(normalized)) {
		return hasNonEmptyString(normalized.value);
	}

	return hasNonEmptyString(normalized.value) || Boolean(normalized.criteria?.some(x => hasNonEmptyString(x.value)));
}

/** Splits a case filter into its first primary criterion and any additional criteria. */
export function extractPrimaryAndAdditionalCriteria(
	filter?: Api.IContactFilterCriteria,
	isDefault = false
): {
	primaryCriteria?: Api.IContactFilterCriteria;
	additionalCriteria?: Api.IContactFilterCriteria[];
} {
	if (isDefault || !filter) {
		return {
			primaryCriteria: undefined,
			additionalCriteria: undefined,
		};
	}

	if (isPrimaryCandidate(filter)) {
		return {
			primaryCriteria: normalizePrimaryCriteria(filter),
			additionalCriteria: undefined,
		};
	}

	const criteriaList = Array.isArray(filter.criteria) ? [...filter.criteria] : [];
	if (criteriaList.length === 0) {
		return {
			primaryCriteria: undefined,
			additionalCriteria: undefined,
		};
	}

	const primaryIndex = criteriaList.findIndex(isPrimaryCandidate);
	if (primaryIndex < 0) {
		return {
			primaryCriteria: undefined,
			additionalCriteria: criteriaList,
		};
	}

	const primary = normalizePrimaryCriteria(criteriaList[primaryIndex]);
	const additional = criteriaList.filter((_, idx) => idx !== primaryIndex);

	return {
		primaryCriteria: primary,
		additionalCriteria: additional.length > 0 ? additional : undefined,
	};
}

export function buildTagAdditionalCriteria(
	orTags: string[],
	andTags: string[],
	notTags: string[]
): Api.IContactFilterCriteria[] {
	const criteriaList: Api.IContactFilterCriteria[] = [];

	if (orTags.length > 0) {
		criteriaList.push({
			criteria: orTags.map(tag => ({
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: tag,
			})),
			op: Api.FilterOperator.Or,
			property: Api.ContactFilterCriteriaProperty.Tag,
		});
	}

	if (andTags.length > 0) {
		criteriaList.push({
			criteria: andTags.map(tag => ({
				property: Api.ContactFilterCriteriaProperty.Tag,
				value: tag,
			})),
			op: Api.FilterOperator.And,
			property: Api.ContactFilterCriteriaProperty.Tag,
		});
	}

	notTags.forEach(tag => {
		criteriaList.push({
			op: Api.FilterOperator.Not,
			property: Api.ContactFilterCriteriaProperty.Tag,
			value: tag,
		});
	});

	return criteriaList;
}

/**
 * Builds the canonical persisted case filter from primary + additional criteria.
 */
export function buildCombinedSegmentFilter(
	primaryCriteria?: Api.IContactFilterCriteria | null,
	additionalCriteria: Api.IContactFilterCriteria[] = []
): Api.IContactFilterCriteria | undefined {
	const normalizedPrimary = normalizePrimaryCriteria(primaryCriteria);
	const normalizedAdditional = (additionalCriteria || []).filter(Boolean);

	const combined = [normalizedPrimary, ...normalizedAdditional].filter((x): x is Api.IContactFilterCriteria =>
		Boolean(x)
	);

	if (combined.length === 0) {
		return undefined;
	}

	return {
		criteria: combined,
		op: Api.FilterOperator.And,
	};
}
